/**
 * @fileoverview File Upload Validation Hook
 * 
 * Provides file validation functionality including type checking,
 * size limits, and content validation for file uploads.
 * 
 * Requirements: 1.1, 1.2, 1.3
 */

import { useCallback, useMemo } from 'react';
import { 
  getFileTypeInfo, 
  isFileTypeAllowed, 
  isFileSizeAllowed, 
  formatFileSize 
} from '../types.js';

/**
 * File upload validation hook
 * 
 * @param {FileUploadConfig} config - Upload configuration
 * @returns {Object} Validation utilities
 */
export function useFileUploadValidation(config) {
  // Validation rules based on configuration
  const validationRules = useMemo(() => ({
    allowedTypes: config.allowedTypes || [],
    maxFileSize: config.maxFileSize || 4 * 1024 * 1024,
    maxFiles: config.maxFiles || 10,
    allowDuplicates: config.allowDuplicates !== false,
    validateContent: config.validateContent !== false
  }), [config]);

  /**
   * Validate a single file
   * 
   * @param {File} file - File to validate
   * @returns {{valid: boolean, errors: string[]}} Validation result
   */
  const validateFile = useCallback((file) => {
    const errors = [];

    // Check if file exists
    if (!file) {
      errors.push('File is required');
      return { valid: false, errors };
    }

    // File name validation
    if (!file.name || file.name.trim() === '') {
      errors.push('File name is required');
    } else if (file.name.length > 255) {
      errors.push('File name is too long (maximum 255 characters)');
    }

    // File type validation
    if (!isFileTypeAllowed(file.name, validationRules.allowedTypes)) {
      const allowedTypesStr = validationRules.allowedTypes
        .map(type => type.toUpperCase())
        .join(', ');
      errors.push(`File type not allowed. Allowed types: ${allowedTypesStr}`);
    }

    // File size validation
    if (file.size === 0) {
      errors.push('File is empty');
    } else if (!isFileSizeAllowed(file.size, validationRules.maxFileSize)) {
      errors.push(
        `File is too large (${formatFileSize(file.size)}). Maximum size: ${formatFileSize(validationRules.maxFileSize)}`
      );
    }

    // File extension validation
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension) {
      errors.push('File must have an extension');
    } else {
      const fileTypeInfo = getFileTypeInfo(extension);
      if (fileTypeInfo) {
        // Check MIME type if available
        if (file.type && !fileTypeInfo.mimeTypes.includes(file.type)) {
          errors.push(
            `File type mismatch. Expected: ${fileTypeInfo.mimeTypes.join(' or ')}, got: ${file.type}`
          );
        }

        // Check file-specific size limits
        if (file.size > fileTypeInfo.maxSize) {
          errors.push(
            `File exceeds size limit for ${extension.toUpperCase()} files (${formatFileSize(file.size)} > ${formatFileSize(fileTypeInfo.maxSize)})`
          );
        }
      }
    }

    // Additional file name checks
    if (file.name.includes('..')) {
      errors.push('File name cannot contain ".."');
    }

    if (/[<>:"|?*]/.test(file.name)) {
      errors.push('File name contains invalid characters');
    }

    if (file.name.startsWith('.')) {
      errors.push('Hidden files are not allowed');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }, [validationRules]);

  /**
   * Validate multiple files
   * 
   * @param {File[]} files - Files to validate
   * @returns {FileValidationResult} Validation result
   */
  const validateFiles = useCallback((files) => {
    const validFiles = [];
    const invalidFiles = [];

    if (!files || !Array.isArray(files)) {
      return {
        validFiles: [],
        invalidFiles: [{ file: null, errors: ['Files must be provided as an array'] }]
      };
    }

    // Check file count limit
    if (files.length > validationRules.maxFiles) {
      return {
        validFiles: [],
        invalidFiles: [{
          file: null,
          errors: [`Too many files selected. Maximum: ${validationRules.maxFiles}, selected: ${files.length}`]
        }]
      };
    }

    // Track file names for duplicate checking
    const fileNames = new Set();
    const duplicateNames = new Set();

    // Validate each file
    files.forEach((file, index) => {
      const validation = validateFile(file);

      // Check for duplicates if not allowed
      if (!validationRules.allowDuplicates) {
        if (fileNames.has(file.name)) {
          duplicateNames.add(file.name);
          validation.errors.push('Duplicate file name');
          validation.valid = false;
        } else {
          fileNames.add(file.name);
        }
      }

      if (validation.valid) {
        validFiles.push(file);
      } else {
        invalidFiles.push({
          file,
          errors: validation.errors,
          index
        });
      }
    });

    // Add duplicate errors to all instances
    if (duplicateNames.size > 0) {
      files.forEach((file, index) => {
        if (duplicateNames.has(file.name)) {
          const existingInvalid = invalidFiles.find(invalid => invalid.index === index);
          if (existingInvalid) {
            if (!existingInvalid.errors.includes('Duplicate file name')) {
              existingInvalid.errors.push('Duplicate file name');
            }
          } else {
            invalidFiles.push({
              file,
              errors: ['Duplicate file name'],
              index
            });
          }
        }
      });

      // Remove valid files that are duplicates
      const validFilesFiltered = validFiles.filter(file => !duplicateNames.has(file.name));
      return {
        validFiles: validFilesFiltered,
        invalidFiles
      };
    }

    return {
      validFiles,
      invalidFiles
    };
  }, [validationRules, validateFile]);

  /**
   * Validate file content (basic checks)
   * 
   * @param {File} file - File to validate
   * @returns {Promise<{valid: boolean, errors: string[]}>} Validation result
   */
  const validateFileContent = useCallback(async (file) => {
    if (!validationRules.validateContent) {
      return { valid: true, errors: [] };
    }

    const errors = [];

    try {
      const extension = file.name.split('.').pop()?.toLowerCase();

      // Text-based file validation
      if (['txt', 'html', 'json', 'csv'].includes(extension)) {
        const text = await readFileAsText(file);
        
        // Check for extremely long lines (potential issue)
        const lines = text.split('\n');
        const maxLineLength = 10000; // 10KB per line
        const longLines = lines.filter(line => line.length > maxLineLength);
        
        if (longLines.length > 0) {
          errors.push(`File contains ${longLines.length} extremely long line(s) which may cause issues`);
        }

        // JSON-specific validation
        if (extension === 'json') {
          try {
            JSON.parse(text);
          } catch (jsonError) {
            errors.push('Invalid JSON format');
          }
        }

        // HTML-specific validation
        if (extension === 'html') {
          // Check for potentially dangerous content
          const dangerousPatterns = [
            /<script[^>]*>.*?<\/script>/gi,
            /javascript:/gi,
            /vbscript:/gi,
            /on\w+\s*=/gi
          ];

          const hasDangerousContent = dangerousPatterns.some(pattern => 
            pattern.test(text)
          );

          if (hasDangerousContent) {
            errors.push('HTML file contains potentially dangerous content (scripts, event handlers)');
          }
        }

        // CSV-specific validation
        if (extension === 'csv') {
          const lines = text.split('\n').filter(line => line.trim());
          if (lines.length === 0) {
            errors.push('CSV file is empty');
          } else {
            // Check for consistent column count
            const firstLineColumns = lines[0].split(',').length;
            const inconsistentLines = lines.slice(1).filter(line => 
              line.split(',').length !== firstLineColumns
            );

            if (inconsistentLines.length > 0) {
              errors.push(`CSV file has inconsistent column count (expected ${firstLineColumns} columns)`);
            }
          }
        }
      }

      // Binary file validation (basic checks)
      if (['pdf', 'docx'].includes(extension)) {
        // Check file signature
        const buffer = await readFileAsArrayBuffer(file, 0, 10);
        const bytes = new Uint8Array(buffer);
        
        if (extension === 'pdf') {
          // PDF should start with %PDF
          const pdfSignature = [0x25, 0x50, 0x44, 0x46]; // %PDF
          const hasValidSignature = pdfSignature.every((byte, index) => 
            bytes[index] === byte
          );
          
          if (!hasValidSignature) {
            errors.push('File does not appear to be a valid PDF');
          }
        }

        if (extension === 'docx') {
          // DOCX should start with ZIP signature (PK)
          const zipSignature = [0x50, 0x4B]; // PK
          const hasValidSignature = zipSignature.every((byte, index) => 
            bytes[index] === byte
          );
          
          if (!hasValidSignature) {
            errors.push('File does not appear to be a valid DOCX document');
          }
        }
      }

    } catch (error) {
      console.error('Content validation error:', error);
      errors.push('Failed to validate file content');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }, [validationRules]);

  /**
   * Get validation summary for multiple files
   * 
   * @param {File[]} files - Files to summarize
   * @returns {Object} Validation summary
   */
  const getValidationSummary = useCallback((files) => {
    const result = validateFiles(files);
    
    const totalSize = result.validFiles.reduce((sum, file) => sum + file.size, 0);
    const typeCount = result.validFiles.reduce((acc, file) => {
      const extension = file.name.split('.').pop()?.toLowerCase();
      acc[extension] = (acc[extension] || 0) + 1;
      return acc;
    }, {});

    return {
      totalFiles: files.length,
      validFiles: result.validFiles.length,
      invalidFiles: result.invalidFiles.length,
      totalSize: formatFileSize(totalSize),
      typeBreakdown: typeCount,
      errors: result.invalidFiles.flatMap(invalid => invalid.errors),
      canProceed: result.validFiles.length > 0
    };
  }, [validateFiles]);

  return {
    validationRules,
    validateFile,
    validateFiles,
    validateFileContent,
    getValidationSummary
  };
}

/**
 * Read file as text
 * 
 * @param {File} file - File to read
 * @returns {Promise<string>} File content as text
 */
function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

/**
 * Read file as array buffer
 * 
 * @param {File} file - File to read
 * @param {number} [start=0] - Start position
 * @param {number} [length] - Number of bytes to read
 * @returns {Promise<ArrayBuffer>} File content as array buffer
 */
function readFileAsArrayBuffer(file, start = 0, length) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    
    const blob = length ? file.slice(start, start + length) : file.slice(start);
    reader.readAsArrayBuffer(blob);
  });
}
